import React, { useEffect, useState } from "react";
import Navbar from "./Navbar";
import moment from "moment";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Card, CardHeader, Button, Table, Spinner, Row, Col } from "react-bootstrap";
import { API_ENDPOINTS } from './config/api';
import PageTitle from './components/PageTitle';
import { buildPrintDocument, openPrintWindow } from './utils/printUtility';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';

const ResidentWorkLogDetail = () => {
  const { residentId, month, year } = useParams();
  const [weeks, setWeeks] = useState([]);
  const [resident, setResident] = useState({});
  const [vacationDays, setVacationDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartType, setChartType] = useState('bar'); // 'bar' or 'line'
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const fromHash = params.get("from");

  const getMonthName = (monthNumber) => {
    // monthNumber is 0-indexed (0-11) from useParams
    const date = new Date(2000, parseInt(monthNumber), 1);
    return date.toLocaleString('default', { month: 'long' });
  };

  // Process weeks data into daily chart data
  const getDailyChartData = () => {
    if (!weeks || weeks.length === 0) return [];
    
    const dailyData = [];
    weeks.forEach((week) => {
      const weekStart = moment(week.weekStart).startOf('day');
      week.days.forEach((day, dayIndex) => {
        if (day && (day.total || day.sick || day.vacation)) {
          // Use the date from the day object if available, otherwise calculate from weekStart
          const dayDate = day.date ? moment(day.date).startOf('day') : weekStart.clone().add(dayIndex, 'days');
          let hours = 0;
          
          if (day.total) {
            const [h, m] = day.total.split(":").map(Number);
            hours = h + (m / 60);
          }
          
          dailyData.push({
            date: dayDate.format('YYYY-MM-DD'),
            day: dayDate.format('MMM D'),
            hours: Math.round(hours * 100) / 100,
            hasData: true,
            isWeekend: dayDate.day() === 0 || dayDate.day() === 6
          });
        }
      });
    });
    
    // Sort by date
    return dailyData.sort((a, b) => moment(a.date).diff(moment(b.date)));
  };

  const handlePrint = () => {
    const residentName = `${resident.firstName} ${resident.lastName}`;
    const monthName = getMonthName(month);
    const currentDate = moment().format('MMMM DD, YYYY hh:mm A');
    const logoUrl = `${window.location.origin}/images/logo3.svg`;

    // Build table HTML
    let tableRows = '';
    if (weeks.length === 0) {
      tableRows = '<tr><td colspan="9" style="text-align: center; padding: 20px;">No work logs found for this period</td></tr>';
    } else {
      tableRows = weeks.map((week, wIdx) => {
        const weekTotalMins = (() => {
          const [h, m] = week.weekTotal.split(":").map(Number);
          return h * 60 + m;
        })();
        const isViolation = weekTotalMins > 4800;
        
        const weekStart = moment(week.weekStart);
        const weekEnd = moment(week.weekStart).add(6, "days");
        const weekNumber = weekStart.format("w");
        const weekRange = `${weekStart.format("MMM D")} - ${weekEnd.format("MMM D, YYYY")}`;
        
        const dayCells = week.days.map((day, dIdx) => {
          if (day && day.sick) {
            return '<td style="text-align: center; padding: 10px;"><span class="badge badge-warning">Sick Leave</span><br><span style="font-size: 11px; color: #666;">0h 00m</span></td>';
          } else if (day && day.vacation) {
            return '<td style="text-align: center; padding: 10px;"><span class="badge badge-info">Vacation Leave</span><br><span style="font-size: 11px; color: #666;">0h 00m</span></td>';
          } else if (day && day.total) {
            const [hours, minutes] = day.total.split(":");
            return `<td style="text-align: center; padding: 10px;"><span style="font-weight: 600; font-size: 14px;">${day.total}</span><br><span style="font-size: 11px; color: #666;">${hours}h ${minutes}m</span></td>`;
          } else {
            return '<td style="text-align: center; padding: 10px;"></td>';
          }
        }).join('');

        const violationBadge = isViolation
          ? '<span class="badge badge-danger" style="margin-left: 8px;">⚠ Violation</span>'
          : '<span class="badge badge-success" style="margin-left: 8px;">✓ Compliant</span>';

        return `<tr>
          <td style="text-align: left; padding: 10px; font-weight: 600;">
            <div>Week ${weekNumber}</div>
            <div style="font-size: 11px; color: #666; margin-top: 4px;">${weekRange}</div>
          </td>
          ${dayCells}
          <td style="text-align: center; padding: 10px; border-left: 1px solid #ddd;">
            <div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
              <span style="font-weight: bold; font-size: 14px;">${week.weekTotal}</span>
              <span style="font-size: 12px; color: #666;">/ 80 Hrs</span>
              ${violationBadge}
            </div>
          </td>
        </tr>`;
      }).join('');

      // Calculate monthly total
      const monthlyTotalMins = weeks.reduce((total, week) => {
        const weekTotal = week.weekTotal.split(":");
        return total + (parseInt(weekTotal[0]) * 60 + parseInt(weekTotal[1]));
      }, 0);
      const monthlyHours = Math.floor(monthlyTotalMins / 60);
      const monthlyMinutes = monthlyTotalMins % 60;
      const monthlyTotal = `${monthlyHours}:${monthlyMinutes.toString().padStart(2, "0")} hrs`;

      tableRows += `<tr style="font-weight: 600; background-color: #f8f9fb;">
        <td colspan="8" style="text-align: right; padding: 15px; font-size: 14px;">Monthly Total:</td>
        <td style="padding: 15px; font-size: 16px; font-weight: bold; border-left: 1px solid #ddd;">${monthlyTotal}</td>
      </tr>`;
    }

    const tableSection = `
      <div class="print-section">
        <h2>Work Log Summary for ${monthName} ${year}</h2>
        <table>
          <thead>
            <tr>
              <th>Week</th>
              <th>Sun</th>
              <th>Mon</th>
              <th>Tue</th>
              <th>Wed</th>
              <th>Thu</th>
              <th>Fri</th>
              <th>Sat</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </div>
    `;

    const customStyles = `
      @page {
        size: landscape;
        margin: 1cm;
      }
      
      table {
        width: 100%;
        border-collapse: collapse;
        font-size: 12px;
      }
      
      thead {
        background: var(--gray-100);
        color: var(--gray-800);
        border-bottom: 2px solid var(--border-bg);
      }
      
      th {
        padding: 12px;
        text-align: center;
        font-weight: 600;
      }
      
      th:first-child {
        text-align: left;
        padding-left: 20px;
      }
      
      tbody tr {
        border-bottom: 1px solid var(--border-bg);
      }
      
      tbody tr:nth-child(even) {
        background: var(--gray-100);
      }
      
      td {
        padding: 10px;
        text-align: center;
        color: var(--gray-700);
      }
      
      td:first-child {
        text-align: left;
        font-weight: 600;
        color: var(--gray-800);
      }
      
      .badge {
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 11px;
        font-weight: bold;
        display: inline-block;
      }
      
      .badge-warning {
        background-color: var(--yellow);
        color: #000;
      }
      
      .badge-info {
        background-color: var(--indigo);
        color: white;
      }
      
      .badge-success {
        background-color: var(--green);
        color: white;
      }
      
      .badge-danger {
        background-color: var(--red);
        color: white;
      }
    `;

    const printContent = buildPrintDocument({
      title: `Work Log Detail - ${residentName}`,
      content: tableSection,
      headerTitle: 'Resident Work Log Detail Report',
      headerSubtitle: '',
      logoUrl,
      showPrintButton: true,
      additionalHeaderInfo: [
        { label: 'Resident', value: residentName },
        { label: 'Period', value: `${monthName} ${year}` },
        { label: 'Generated', value: currentDate }
      ],
      footerMessage: 'This is a system-generated report. For questions, please contact the program director.',
      customStyles,
      pageSize: 'landscape'
    });

    openPrintWindow(printContent);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setError("No authentication token found");
          setLoading(false);
          return;
        }

        const res = await fetch(
          `${API_ENDPOINTS.DIRECTOR}/work-log-detail?residentId=${residentId}&month=${month}&year=${year}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!res.ok) {
          throw new Error(`Failed to fetch data: ${res.status}`);
        }

        const data = await res.json();
        if (!data.resident) {
          throw new Error("Resident not found");
        }

        setResident(data.resident);
        setWeeks(data.weeks || []);
        
        // Process vacation days
        if (data.vacations) {
          const days = [];
          data.vacations.forEach(vac => {
            let curr = moment(vac.start);
            const end = moment(vac.end);
            while (curr.isSameOrBefore(end)) {
              days.push({
                date: curr.format('YYYY-MM-DD'),
                type: vac.type || 'Vacation'
              });
              curr.add(1, 'day');
            }
          });
          setVacationDays(days);
        } else {
          setVacationDays([]);
        }

        console.log('Vacations:', data.vacations);
        console.log('Weeks:', data.weeks);
      } catch (err) {
        setError(err.message);
        setWeeks([]);
        setResident({});
        setVacationDays([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [residentId, month, year]);

  if (loading) {
    return (
      <div style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <PageTitle title="Loading..." />
        <div className="text-center">
          <Spinner animation="border" variant="primary" className="mb-2" />
          <div className="text-muted">Loading work log details...</div>
        </div>
      </div>
    );
  }
  if (error) return <div>Error: {error}</div>;
  if (!resident.firstName) return <div>Resident not found</div>;

  return (
    <div>
      <PageTitle title="Work Log Detail" description="Detailed view of resident work hours and compliance" />
      <Navbar pageTitle="Resident Work Log" />
      <div className="bg-white shadow-sm custom-sticky-top">
        <Container>
          <Row>
            <Col className="d-flex align-items-center justify-content-between">
              <h6 className="mb-0 fw-semibold py-3">Resident Work Log Detail For: <span className="fw-bold">{resident.firstName} {resident.lastName}</span></h6>
              <Button variant="outline-primary" size="sm" onClick={handlePrint}>
                <i className="bi bi-printer me-2"></i>
                Print Report
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
      <Container className="py-4">

          <Card className="shadow-sm border-0 mb-4">
            <CardHeader className="p-4 border-bottom-bottom " style={{backgroundColor: "var(--bs-card-bg)"}}>
              <h6 className="fw-bold">Work Log Summary for the month of {getMonthName(month)} {year}</h6>
            </CardHeader>
            <Card.Body className="p-0">
              {/* Bar Chart Section */}
              {weeks.length > 0 && (() => {
                const chartData = getDailyChartData();
                return (
                  <div className="p-4 border-bottom">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="text-muted mb-0 fw-semibold">
                        Daily Hours Worked - {getMonthName(month)} {year}
                      </h6>
                      <div className="radio-inputs" style={{ maxWidth: '82px' }}>
                        <label className="radio">
                          <input
                            type="radio"
                            name="chart-type-radio"
                            checked={chartType === 'bar'}
                            onChange={() => setChartType('bar')}
                            aria-label="Bar Chart"
                          />
                          <span className="name"><i className="bi bi-bar-chart"></i></span>
                        </label>
                        <label className="radio">
                          <input
                            type="radio"
                            name="chart-type-radio"
                            checked={chartType === 'line'}
                            onChange={() => setChartType('line')}
                            aria-label="Line Chart"
                          />
                          <span className="name"><i className="bi bi-graph-up"></i></span>
                        </label>
                      </div>
                    </div>
                    <div className="rounded p-3">
                      {chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                          {chartType === 'bar' ? (
                            <BarChart 
                              data={chartData} 
                              margin={{ top: 0, right: 0, left: 0, bottom: 0}}
                            >
                              <CartesianGrid strokeDasharray="2 6" stroke="var(--border-bg)" vertical={false} />
                              <XAxis 
                                dataKey="day" 
                                angle={-45} 
                                textAnchor="end" 
                                height={35}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: 'var(--gray-600)' }}
                                axisLine={{ stroke: 'var(--border-bg)', strokeWidth: 1 }}
                                interval="preserveStartEnd"
                                minTickGap={20}
                              />
                              <YAxis 
                                tick={{ fontSize: 12, fill: 'var(--gray-600)' }}
                                tickLine={false}
                                axisLine={{ stroke: 'var(--border-bg)', strokeWidth: 1 }}
                                label={{ value: 'Hours', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                              />
                              <Tooltip 
                                contentStyle={{
                                  backgroundColor: 'var(--card-bg)',
                                  border: '1px solid var(--border-bg)',
                                  color: 'var(--gray-800)',
                                  borderRadius: '8px',
                                  boxShadow: 'var(--box-shadow-sm)'
                                }}
                                cursor={{ fill: 'var(--gray-100)' }}
                                formatter={(value, name) => [value + ' hours', 'Hours Worked']}
                                labelFormatter={(label) => {
                                  const dayData = chartData.find(d => d.day === label);
                                  return dayData ? `${label} (${dayData.date})` : label;
                                }}
                              />
                              <Bar 
                                dataKey="hours" 
                                fill="var(--blue)"
                                radius={[4, 4, 0, 0]}
                                barSize={20}
                                cursor={{ fill: 'var(--gray-100)' }}
                              >
                                {chartData.map((entry, index) => (
                                  <Cell 
                                    key={`cell-${index}`} 
                                    fill={entry.isWeekend ? 'var(--red)' : (entry.hours > 8 ? 'var(--yellow)' : 'var(--green)')}
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          ) : (
                            <LineChart 
                              data={chartData} 
                              margin={{ top: 0, right: 0, left: 0, bottom: 0}}
                            >
                              <CartesianGrid strokeDasharray="2 6" stroke="var(--border-bg)" vertical={false} />
                              <XAxis 
                                dataKey="day" 
                                angle={-45} 
                                textAnchor="end" 
                                height={35}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: 'var(--gray-600)' }}
                                axisLine={{ stroke: 'var(--border-bg)', strokeWidth: 1 }}
                                interval="preserveStartEnd"
                                minTickGap={20}
                              />
                              <YAxis 
                                tick={{ fontSize: 12, fill: 'var(--gray-600)' }}
                                tickLine={false}
                                axisLine={{ stroke: 'var(--border-bg)', strokeWidth: 1 }}
                                label={{ value: 'Hours', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                              />
                              {/* Solid reference line at y=8 */}
                              <ReferenceLine y={8} stroke="var(--red-light)" strokeWidth={1} strokeDasharray="0" ifOverflow="extendDomain" />
                              <Tooltip 
                                contentStyle={{
                                  backgroundColor: 'var(--card-bg)',
                                  border: '1px solid var(--border-bg)',
                                  color: 'var(--gray-800)',
                                  borderRadius: '8px',
                                  boxShadow: 'var(--box-shadow-sm)'
                                }}
                                cursor={{ stroke: 'var(--gray-400)', strokeWidth: 1 }}
                                formatter={(value, name) => [value + ' hours', 'Hours Worked']}
                                labelFormatter={(label) => {
                                  const dayData = chartData.find(d => d.day === label);
                                  return dayData ? `${label} (${dayData.date})` : label;
                                }}
                              />
                              <Line 
                                type="monotone" 
                                dataKey="hours" 
                                stroke="var(--blue)" 
                                strokeWidth={2}
                                dot={{ fill: 'var(--blue)', r: 4 }}
                                activeDot={{ r: 6 }}
                              />
                            </LineChart>
                          )}
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center py-4 text-muted">
                          <i className="bi bi-bar-chart" style={{ fontSize: 48, opacity: 0.3 }}></i>
                          <p className="mt-2">No work log data available</p>
                        </div>
                      )}
                    </div>

                    {/* Legend */}
                    {chartType === 'bar' && (
                      <div className="d-flex justify-content-center gap-4 flex-wrap mt-3">
                      <div className="d-flex align-items-center">
                        <div className="rounded me-2" style={{ width: 12, height: 12, backgroundColor: 'var(--bs-success)'}}></div>
                        <small className="text-muted">Normal Hours (≤8)</small>
                      </div>
                      <div className="d-flex align-items-center">
                        <div className="rounded me-2" style={{ width: 12, height: 12, backgroundColor: 'var(--bs-warning)'}}></div>
                        <small className="text-muted">Long Hours (&gt;8)</small>
                      </div>
                      <div className="d-flex align-items-center">
                        <div className="rounded me-2" style={{ width: 12, height: 12, backgroundColor: 'var(--bs-danger)'}}></div>
                        <small className="text-muted">Weekend</small>
                      </div>
                    </div>
                    )}
                  </div>
                );
              })()}
              
              {/* Summary Statistics Section */}
              {weeks.length > 0 && (() => {
                const chartData = getDailyChartData();
                const totalHours = chartData.reduce((total, day) => total + day.hours, 0);
                const daysWorked = chartData.filter(day => day.hasData).length;
                const avgHoursPerDay = daysWorked > 0 ? totalHours / daysWorked : 0;
                const daysOver8Hours = chartData.filter(day => day.hours > 8).length;
                const weekendDays = chartData.filter(day => day.isWeekend && day.hasData).length;
                
                // Calculate total days in the month for attendance
                // month is 0-indexed (0-11), so we need to add 1 for the actual month number
                const monthNum = parseInt(month);
                const monthStart = moment().year(parseInt(year)).month(monthNum).startOf('month');
                const monthEnd = moment(monthStart).endOf('month');
                const totalDaysInMonth = monthEnd.diff(monthStart, 'days') + 1;
                const attendance = totalDaysInMonth > 0 ? Math.round((daysWorked / totalDaysInMonth) * 100) : 0;
                
                return (
                  <div className="p-4 border-bottom bg-light">
                    <div className="d-flex justify-content-between align-items-start gap-4 flex-wrap">
                      <div className="flex-fill">
                        <div className="text-muted small mb-1">Days Worked</div>
                        <div className="fw-bold" style={{ fontSize: '24px' }}>
                          {daysWorked}
                        </div>
                      </div>
                      <div className="flex-fill">
                        <div className="text-muted small mb-1">Total Hours</div>
                        <div className="fw-bold" style={{ fontSize: '24px' }}>
                          {Math.round(totalHours * 100) / 100}
                        </div>
                      </div>
                      <div className="flex-fill">
                        <div className="text-muted small mb-1">Avg Hours/Day</div>
                        <div className="fw-bold" style={{ fontSize: '24px' }}>
                          {Math.round(avgHoursPerDay * 100) / 100}
                        </div>
                      </div>
                      <div className="flex-fill">
                        <div className="text-muted small mb-1">Days &gt; 8 Hours</div>
                        <div className="fw-bold" style={{ fontSize: '24px' }}>
                          {daysOver8Hours}
                        </div>
                      </div>
                      <div className="flex-fill">
                        <div className="text-muted small mb-1">Weekend Days</div>
                        <div className="fw-bold" style={{ fontSize: '24px' }}>
                          {weekendDays}
                        </div>
                      </div>
                      <div className="flex-fill">
                        <div className="text-muted small mb-1">Attendance</div>
                        <div className="fw-bold" style={{ fontSize: '24px' }}>
                          {attendance}%
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
        {weeks.length === 0 ? (
          <div className="p-5 text-center text-secondary bg-light">
          <div className="fs-1 mb-3 text-secondary-emphasis">📅</div>
            No work logs found for this period
            <div className="mt-3 text-muted fs-6">
              Work logs will appear here once submitted
            </div>
          </div>
        ) : (
        <Table responsive hover className="mb-0">
            <thead>
              <tr className="custom-header-bg">
              <th className="px-4 text-start">Week</th>
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Total"].map((d, i) => (
                <th key={i} className="px-4 text-center">{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, wIdx) => {
                const weekTotalMins = (() => {
                  const [h, m] = week.weekTotal.split(":").map(Number);
                  return h * 60 + m;
                })();
                const isViolation = weekTotalMins > 4800;
                return (
                  <tr key={wIdx}>
                    <td className="px-4 fw-medium border-end">
                      <div className="fs-6">Week {moment(week.weekStart).format("w")}</div>
                      <div className="mt-1 text-muted">
                        {moment(week.weekStart).format("MMM D")} - {moment(week.weekStart).add(6, "days").format("MMM D, YYYY")}
                      </div>
                    </td>
                    {week.days.map((day, dIdx) => (
                      <td key={dIdx} className="p-3 text-center position-relative">
                        {day && day.sick ? (
                          <div className="d-flex flex-column align-items-center">
                            <span className="badge bg-warning text-dark mb-1">Sick Leave</span>
                            <span className="text-muted fs-7">0h 00m</span>
                          </div>
                        ) : day && day.vacation ? (
                          <div className="d-flex flex-column align-items-center">
                            <span className="badge bg-info text-white mb-1">Vacation Leave</span>
                            <span className="text-muted fs-7">0h 00m</span>
                          </div>
                        ) : day && day.total ? (
                          <div className="d-flex flex-column align-items-center">
                            <span className="fs-6 fw-medium">{day.total}</span>
                            <span className="fs-7 text-muted mt-1">
                              {day.total.split(":")[0]}h {day.total.split(":")[1]}m
                            </span>
                          </div>
                        ) : null}
                      </td>
                    ))}
                    <td className="border-start">
                      <div className="d-flex flex-row align-items-center gap-1 justify-content-center">
                        <span className="p-1 fs-6">
                          <span className="fw-bold fs-5">{week.weekTotal}</span> / 80 Hrs
                        </span>
                      {isViolation ? (
                        <div className="text-danger fw-bold rounded p-1 px-2 small bg-danger">
                          <i className="bi bi-exclamation-triangle-fill text-danger me-1"></i> Violation
                        </div>
                      ) : (
                        <div className="rounded p-1 px-2 bg-success small">
                          <i className="bi bi-check-lg me-1 text-success "></i> Compliant
                        </div>
                      )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              <tr className="fw-medium">
              <td colSpan="8" className="bg-light text-end p-4 fs-6">Monthly Total:</td>
                <td className="p-4 fs-5 fw-bold border-start">
                  {Math.floor(weeks.reduce((total, week) => {
                    const weekTotal = week.weekTotal.split(":");
                    return total + (parseInt(weekTotal[0]) * 60 + parseInt(weekTotal[1]));
                  }, 0) / 60)}:{(
                    weeks.reduce((total, week) => {
                      const weekTotal = week.weekTotal.split(":");
                      return total + (parseInt(weekTotal[0]) * 60 + parseInt(weekTotal[1]));
                    }, 0) % 60
                ).toString().padStart(2, "0")} hrs
                </td>
              </tr>
            </tbody>
          </Table>
        )}
        </Card.Body>
        </Card>
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            if (fromHash) {
              navigate(`/director/work-logs${fromHash}`);
            } else {
              navigate(`/director/work-logs?month=${parseInt(month) - 1}&year=${year}`);
            }
          }}
        >
          Return to Director Work Log
        </Button>
      </Container>
    </div>
  );
};

export default ResidentWorkLogDetail;
